import * as React from 'react';
import { memo } from 'react';
import { CellProps, Row, useTable } from 'react-table';
import { useTranslation } from 'react-i18next';

import styled, { css } from 'styled-components';

import { Gradients, IconSizes } from 'components/types';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  IconsNames,
  Icon,
  Title,
} from 'atoms';

import { LoadingData, LoadingTable, InfiniteScrollContainer } from 'molecules';

import { Colors } from 'services/ColorService';

type Accessors =
  | 'fullName'
  | 'phone'
  | 'email'
  | 'courses'
  | 'availability'
  | 'rating';

export type IRow = {
  [key in Accessors]: React.ReactNode;
} & {
  id: string;
  isDismissed: boolean;
  accessToLms: boolean;
  availability: string;
  rating: number | null;
};

export interface IColumn {
  accessor: Accessors;
  flexible?: boolean;
  align?: 'left' | 'right' | 'center';
  Header?: string | React.ReactElement;
  columns?: IColumn[];
  marginRight?: string;
  marginLeft?: string;
  columnWidth?: string;
  /** Optional custom cell renderer (react-table). */
  Cell?: (props: CellProps<IRow>) => React.ReactNode;
}

export interface ITableTeachersComponent {
  columns: IColumn[];
  rows: IRow[];
  isFlexible: boolean;
  noDataText: string;
  loadMore: () => void;
  hasMore: boolean;
  onEditButtonClick: (teacherId: string) => void;
  onClickAddApplication: (teacherId: string) => void;
  /** Row-level click → navigate to `PATHS.teacherDetail`. */
  onClickOpenDetail: (teacherId: string) => void;
  loading?: boolean;
  /**
   * «Заявка» и «Редактировать» в строке — роли карточки преподавателя.
   * Методист список читает (решение владельца 09.10), а мутации ему сервер
   * не даёт — кнопок у него нет.
   */
  showRowActions?: boolean;
  /**
   * Значок и тон строки «нет доступа к LMS»: `accessToLms` — служебное поле,
   * методисту приходит `false` у всех, и отметка висела бы на каждой строке.
   */
  showLmsAccess?: boolean;
}

export const TableTeachersComponent = memo(
  ({
    isFlexible,
    noDataText,
    rows: rowsSource,
    columns,
    loadMore,
    hasMore,
    onEditButtonClick,
    onClickAddApplication,
    onClickOpenDetail,
    loading,
    showRowActions = true,
    showLmsAccess = true,
  }: ITableTeachersComponent) => {
    const { t } = useTranslation();
    const {
      getTableProps,
      headers,
      getTableBodyProps,
      rows,
      prepareRow,
    } = useTable({
      columns,
      data: rowsSource,
    });

    return (
      <Wrapper>
        <InfiniteScrollContainer
          loadMore={loadMore}
          hasMore={hasMore}
          dataLength={rows.length}
          loading={loading || false}
        >
          <Table {...getTableProps()} flexible={isFlexible} rowShadowOnHover>
            <TableHead>
              <TableRow type="row">
                {headers.map((column: any) => {
                  const {
                    key: cellKey,
                    ...restCellProps
                  } = column.getHeaderProps({
                    type: 'headerCell',
                    flexible: column.flexible && isFlexible,
                    width: column.columnWidth,
                    margin: {
                      right: column.marginRight,
                      left: column.marginLeft,
                    },
                  });

                  return (
                    <TableCell key={cellKey} {...restCellProps}>
                      {column.render('Header')}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody {...getTableBodyProps()}>
              {!rows.length && !loading && (
                <TableRow key="noData">
                  <TableCell type="cell" width="100%" align="center">
                    {noDataText}
                  </TableCell>
                </TableRow>
              )}
              {!rows.length && loading && (
                <TableRow>
                  <TableCell width="100%" align="center">
                    <LoadingTable />
                  </TableCell>
                </TableRow>
              )}
              {Boolean(rows.length) &&
                rows.map((row: Row<IRow>) => {
                  prepareRow(row);
                  const { key: rowKey, ...restRowProps } = row.getRowProps();

                  const teacherId = row.original.id;
                  return (
                    <TeachersTableRow
                      key={rowKey}
                      type="row"
                      isDismissed={row.original.isDismissed}
                      // Без права на служебные поля `accessToLms` — пустое
                      // `false`, а не «нет доступа»: строку не тонируем.
                      accessToLms={!showLmsAccess || row.original.accessToLms}
                      clickable
                      onClick={() => onClickOpenDetail(teacherId)}
                      {...restRowProps}
                    >
                      {row.cells.map((cell: any) => {
                        const {
                          key: cellKey,
                          ...restCellProps
                        } = cell.getCellProps({
                          width: cell.column.columnWidth,
                          flexible: cell.column.flexible && isFlexible,
                          margin: {
                            right: cell.column.marginRight,
                            left: cell.column.marginLeft,
                          },
                        });

                        const blueText =
                          cell.column.Header ===
                            `${t('table.teachers.phone')}` ||
                          cell.column.Header === `${t('table.teachers.email')}`;

                        if (
                          cell.column.id === 'fullName' &&
                          row.original.isDismissed
                        ) {
                          return (
                            <TableCell
                              key={cellKey}
                              textColor={blueText ? Colors.Blue : Colors.Black}
                              {...restCellProps}
                            >
                              <UsernameWrapper>
                                {cell.render('Cell')}
                                <IconDismissedWrapper>
                                  <IconDismissed name={IconsNames.Error} />
                                  <TitleStyled
                                    text={t('table.teachers.isDismissed')}
                                  />
                                </IconDismissedWrapper>
                              </UsernameWrapper>
                            </TableCell>
                          );
                        }

                        if (
                          cell.column.id === 'fullName' &&
                          showLmsAccess &&
                          !row.original.accessToLms
                        ) {
                          return (
                            <TableCell
                              key={cellKey}
                              textColor={blueText ? Colors.Blue : Colors.Black}
                              {...restCellProps}
                            >
                              <UsernameWrapper>
                                {cell.render('Cell')}
                                <IconWithoutAccessWrapper>
                                  <IconWithoutAccess
                                    name={IconsNames.WarningAccess}
                                  />
                                  <TitleStyled
                                    text={t('table.teachers.accessToLms')}
                                  />
                                </IconWithoutAccessWrapper>
                              </UsernameWrapper>
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell
                            key={cellKey}
                            textColor={blueText ? Colors.Blue : Colors.Black}
                            {...restCellProps}
                          >
                            {cell.render('Cell')}
                          </TableCell>
                        );
                      })}

                      {showRowActions ? (
                        <RowButtons onClick={(e) => e.stopPropagation()}>
                          <IconButtonStyled
                            name={IconsNames.Clip}
                            size={IconSizes.lg}
                            onClick={() => onClickAddApplication(teacherId)}
                          />
                          <WrappedEdit>
                            <IconButtonStyled
                              size={IconSizes.lg}
                              name={IconsNames.Edit}
                              onClick={() => onEditButtonClick(teacherId)}
                            />
                          </WrappedEdit>
                        </RowButtons>
                      ) : null}
                    </TeachersTableRow>
                  );
                })}
            </TableBody>
          </Table>
        </InfiniteScrollContainer>
        {Boolean(rows.length) && loading && <LoadingData />}
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  width: 100%;
  overflow-y: hidden;
  overflow-x: auto;
`;

const TeachersTableRow = styled((props) => (
  <TableRow {...props}>{props.children}</TableRow>
))`
  position: relative;
  ${({ isDismissed, accessToLms }) => {
    if (isDismissed) {
      return css`
        background: ${Gradients.InactiveRed};
      `;
    }
    if (!accessToLms) {
      return css`
        background: #fef8e8;
      `;
    }
  }}
`;

const UsernameWrapper = styled.span`
  position: relative;
  width: auto;
`;

const TitleStyled = styled((props) => <Title {...props} />)`
  display: none;
`;

const IconDismissedWrapper = styled.div`
  position: absolute;
  top: 0;
  left: calc(100% + 16px);
  height: 16px;
  width: 16px;
  cursor: pointer;

  &:hover {
    ${TitleStyled} {
      display: block;
    }
  }
`;

const IconWithoutAccessWrapper = styled.div`
  position: absolute;
  top: 0;
  left: calc(100% + 16px);
  height: 16px;
  width: 16px;
  cursor: pointer;

  &:hover {
    ${TitleStyled} {
      display: block;
    }
  }
`;

const IconDismissed = styled(Icon)`
  height: 100%;
  width: 100%;
`;

const IconWithoutAccess = styled(Icon)`
  height: 100%;
  width: 100%;
`;

const WrappedEdit = styled.div`
  margin-left: 24px;
  height: 32px;
`;

const RowButtons = styled.td`
  position: absolute;
  top: 12px;
  right: 24px;
  bottom: 12px;
  display: none;
  align-items: center;

  ${TeachersTableRow}:hover & {
    display: flex;
  }
`;

const IconButtonStyled = styled((props) => <IconButton {...props} />)`
  display: flex;
  height: 32px;
  width: 32px;
  border-radius: 100%;

  :hover {
    & svg {
      & > path {
        fill: ${Colors.White};
      }
      & > circle {
        fill: ${Colors.Blue};
      }
    }
  }
`;
